import { LengthRange } from './lengthRange.gen';

export enum WarningLevel {
	info = 0,
	warning = 1,
	error = 2,
}

export interface Warning {
	level: WarningLevel;
	title: string;
	message?: string | string[];
}

export const getLevelIcon = (level: WarningLevel): string => {
	switch (level) {
		case WarningLevel.info:
			return 'ℹ️';
		case WarningLevel.warning:
			return '⚠️';
		case WarningLevel.error:
			return '🔴';
	}
};

export const defaultWarningTitle = 'Data exceeds available image range:';
export const warningThreshold = 0.01;

/**
 * Utility method for calculating any warnings to be returned for a given data range with respect to the overall image range
 * @param dataRange The range of data, and if this falls outside the {@see imageRange} then a warning will be returned with a message describing the difference. If undefined, no warning will be returned.
 * @param imageRange The range of the image, and if the {@see dataRange} falls outside this, then a warning will be returned with the message:
 * @param warningTitle An optional title to set in the warning message. Defaults to {@see defaultWarningTitle}
 * @returns warnings to display. If undefined, no warnings should be displayed.
 */
export const getWarningForDataRange = (
	dataRange: LengthRange | undefined,
	imageRange: LengthRange,
	warningTitle?: string,
): Warning | undefined => {
	if (dataRange) {
		const { depthFrom, depthTo } = dataRange;
		// Set the values to truthy values if they are outside the range, with a 1% margin of error to catch slight start/end differences for otherwise identical depth ranges.
		const totalImageRange = imageRange.depthTo - imageRange.depthFrom;
		const startOutOfRange =
			imageRange.depthFrom - totalImageRange * warningThreshold > depthFrom ? depthFrom : undefined;
		const endOutOfRange = imageRange.depthTo + totalImageRange * warningThreshold < depthTo ? depthTo : undefined;
		const rangeMessages = [];
		if (startOutOfRange !== undefined) {
			rangeMessages.push(
				`- Data range start ${startOutOfRange}m starts before the visible depth range of the images ${imageRange.depthFrom}m`,
			);
		}
		if (endOutOfRange !== undefined) {
			rangeMessages.push(
				`- Data range end ${endOutOfRange}m ends after the visible depth range of the images ${imageRange.depthTo}m`,
			);
		}
		if (rangeMessages.length) {
			return {
				level: WarningLevel.warning,
				title: warningTitle ?? defaultWarningTitle,
				message: rangeMessages,
			};
		}
	}
	return undefined;
};
